import type { TFunction } from 'i18next';
import type { BillingSummary } from '../../types';
import type { Column, DataTableFooter } from '../DataTable';
import { formatCurrency, formatNumber } from '../../utils/format';
import { nonBillableHours, revenuePerHour, type BillingTotals } from './financeModel';

/**
 * Customer billing for one month on DataTable v2 (T-41): a wide finance table
 * (`mobile="scroll"`: every column kept on phones, the customer column sticky),
 * numbers at the end with tabular figures, money with 2 decimals, and the
 * totals in the table's `<tfoot>`. The customer name is the row link (the
 * customer page). Two columns are computed here from the same row (A6, for
 * profit per customer): hours not billed (total − billable) and revenue per
 * hour worked (amount ÷ all hours, non-billable included; "—" without hours).
 */

const hours = (n: number) => formatNumber(n, 1);
const num = (v: unknown) => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

export const billingHref = (b: BillingSummary) => `/organizations/${b.organization_id}`;

export function billingColumns({ t }: { t: TFunction }): Column<BillingSummary>[] {
  const cols: Column<BillingSummary>[] = [
    {
      key: 'org',
      header: t('billing.org'),
      link: true,
      render: (row) => <bdi>{row.organization_name}</bdi>,
    },
    {
      key: 'engagements',
      header: t('billing.activeEngagements'),
      render: (row) => {
        const list = row.engagements ?? [];
        if (list.length === 0 && !row.other_amount) return <span className="text-text-3">{t('billing.noActiveEngagement')}</span>;
        return (
          <ul className="space-y-0.5">
            {list.map((e) => (
              <li
                key={e.engagement_id}
                className="flex max-w-[24ch] items-baseline gap-1 whitespace-nowrap text-text-2 xl:max-w-[30ch] 2xl:max-w-[48ch]"
                title={`${t(`engagementDetail.types.${e.type}`, { defaultValue: e.type })}${e.name ? ` · ${e.name}` : ''} · ${formatCurrency(e.amount)}`}
              >
                <span className="min-w-0 truncate">
                  <span>{t(`engagementDetail.types.${e.type}`, { defaultValue: e.type })}</span>
                  {e.name ? <> · <bdi>{e.name}</bdi></> : null}
                </span>
                <span className="shrink-0">·</span>
                <span className={`shrink-0 ${e.amount > 0 ? 'text-text' : 'text-text-3'}`}>{formatCurrency(e.amount)}</span>
              </li>
            ))}
            {row.other_amount ? (
              <li className="whitespace-nowrap text-text-2">
                {t('billing.otherEngagements')} · <span className="text-text">{formatCurrency(row.other_amount)}</span>
              </li>
            ) : null}
          </ul>
        );
      },
    },
    {
      key: 'logs',
      header: t('common.records'),
      align: 'end',
      render: (row) => <span className="whitespace-nowrap text-text-2">{formatNumber(num(row.total_logs))}</span>,
    },
    {
      key: 'hours',
      header: t('billing.totalHours'),
      align: 'end',
      render: (row) => <span className="whitespace-nowrap text-text-2">{hours(num(row.total_hours))}</span>,
    },
    {
      key: 'billable',
      header: t('billing.billableHours'),
      align: 'end',
      render: (row) => <span className="whitespace-nowrap">{hours(num(row.billable_hours))}</span>,
    },
    {
      key: 'nonBillable',
      header: t('finance.billing.colNonBillable'),
      align: 'end',
      render: (row) => {
        const v = nonBillableHours(num(row.total_hours), num(row.billable_hours));
        return <span className={`whitespace-nowrap ${v > 0 ? 'text-text-2' : 'text-text-3'}`}>{hours(v)}</span>;
      },
    },
  ];
  cols.push({
    key: 'amount',
    header: t('billing.amount'),
    align: 'end',
    render: (row) => {
      const v = num(row.total_amount);
      return <span className={`whitespace-nowrap font-medium ${v <= 0 ? 'text-text-3' : 'text-text'}`}>{formatCurrency(v)}</span>;
    },
  });
  cols.push({
    key: 'perHour',
    header: t('finance.billing.colPerHour'),
    align: 'end',
    render: (row) => perHourCell(revenuePerHour(num(row.total_amount), num(row.total_hours)), t),
  });
  return cols;
}

/** Revenue per hour worked, or "—" (with a spoken reason) when no hours were worked. */
function perHourCell(v: number | null, t: TFunction) {
  if (v === null) {
    return (
      <span className="text-text-3">
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t('finance.billing.perHourNone')}</span>
      </span>
    );
  }
  return <span className="whitespace-nowrap text-text-2">{formatCurrency(v)}</span>;
}

/** The totals of exactly the rows shown (`<tfoot>`). */
export function billingFooter(sum: BillingTotals, t: TFunction): DataTableFooter {
  const cell = (v: string) => <span className="whitespace-nowrap">{v}</span>;
  return {
    label: t('finance.totals.label', { count: sum.count }),
    cells: {
      logs: cell(formatNumber(sum.logs)),
      hours: cell(hours(sum.hours)),
      billable: cell(hours(sum.billableHours)),
      nonBillable: cell(hours(nonBillableHours(sum.hours, sum.billableHours))),
      amount: cell(formatCurrency(sum.amount)),
      perHour: perHourCell(revenuePerHour(sum.amount, sum.hours), t),
    },
  };
}
